import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { Shield, Lock, Eye, Database, Mail, Cookie } from 'lucide-react';
import { SEO } from '@/components/seo/SEO';

const seoContent = {
  nl: {
    title: 'Privacybeleid',
    description: 'Lees ons privacybeleid. Bij Netblitz beschermen we uw persoonsgegevens. Informatie over cookies, gegevensverzameling en uw rechten.',
  },
  en: {
    title: 'Privacy Policy',
    description: 'Read our privacy policy. At Netblitz we protect your personal data. Information about cookies, data collection and your rights.',
  },
  fr: {
    title: 'Politique de Confidentialité',
    description: 'Lisez notre politique de confidentialité. Chez Netblitz, nous protégeons vos données personnelles.',
  },
  de: {
    title: 'Datenschutzrichtlinie',
    description: 'Lesen Sie unsere Datenschutzrichtlinie. Bei Netblitz schützen wir Ihre persönlichen Daten.',
  }
};

const translations = {
  nl: {
    title: 'Privacybeleid',
    subtitle: 'Laatst bijgewerkt: maart 2026',
    intro: 'Bij Netblitz hechten wij veel waarde aan de bescherming van uw persoonsgegevens. Dit privacybeleid legt uit welke gegevens we verzamelen, waarom we dat doen en hoe we ermee omgaan.',
    sections: [
      {
        icon: Database,
        title: 'Welke gegevens verzamelen we?',
        content: `We verzamelen de volgende persoonsgegevens:
• Contactgegevens (naam, e-mail, telefoonnummer) via ons contactformulier
• Technische gegevens (IP-adres, browsertype) voor websiteanalyse
• Cookies voor functionaliteit en analytics`
      },
      {
        icon: Eye,
        title: 'Waarom verzamelen we deze gegevens?',
        content: `We gebruiken uw gegevens voor:
• Het beantwoorden van uw vragen en offerteaanvragen
• Het verbeteren van onze website en diensten
• Het versturen van relevante informatie (alleen met uw toestemming)`
      },
      {
        icon: Lock,
        title: 'Hoe beschermen we uw gegevens?',
        content: `We nemen passende maatregelen om uw gegevens te beschermen:
• Versleutelde verbindingen (HTTPS)
• Beveiligde servers
• Beperkte toegang tot persoonsgegevens
• Regelmatige beveiligingsupdates`
      },
      {
        icon: Cookie,
        title: 'Cookies',
        content: `Onze website gebruikt cookies voor:
• Noodzakelijke cookies: voor de werking van de website
• Analytische cookies: om bezoekersstatistieken te begrijpen
• Marketing cookies: alleen met uw expliciete toestemming

U kunt uw cookievoorkeuren op elk moment aanpassen.`
      },
      {
        icon: Shield,
        title: 'Uw rechten',
        content: `U heeft het recht om:
• Inzage te vragen in uw persoonsgegevens
• Correctie of verwijdering te vragen
• Bezwaar te maken tegen verwerking
• Een klacht in te dienen bij de toezichthouder

Neem contact met ons op voor vragen over uw gegevens.`
      },
      {
        icon: Mail,
        title: 'Contact',
        content: `Voor vragen over dit privacybeleid kunt u contact opnemen via:
• E-mail: hello@netblitz.eu
• Telefoon: +31 85 115 5703
• Adres: Robert Schumandomein 2, 6229ES Maastricht, Nederland`
      }
    ]
  },
  en: {
    title: 'Privacy Policy',
    subtitle: 'Last updated: March 2026',
    intro: 'At Netblitz, we value the protection of your personal data. This privacy policy explains what data we collect, why we collect it, and how we handle it.',
    sections: [
      {
        icon: Database,
        title: 'What data do we collect?',
        content: `We collect the following personal data:
• Contact details (name, email, phone number) via our contact form
• Technical data (IP address, browser type) for website analysis
• Cookies for functionality and analytics`
      },
      {
        icon: Eye,
        title: 'Why do we collect this data?',
        content: `We use your data for:
• Responding to your questions and quote requests
• Improving our website and services
• Sending relevant information (only with your consent)`
      },
      {
        icon: Lock,
        title: 'How do we protect your data?',
        content: `We take appropriate measures to protect your data:
• Encrypted connections (HTTPS)
• Secure servers
• Limited access to personal data
• Regular security updates`
      },
      {
        icon: Cookie,
        title: 'Cookies',
        content: `Our website uses cookies for:
• Necessary cookies: for website functionality
• Analytical cookies: to understand visitor statistics
• Marketing cookies: only with your explicit consent

You can adjust your cookie preferences at any time.`
      },
      {
        icon: Shield,
        title: 'Your rights',
        content: `You have the right to:
• Request access to your personal data
• Request correction or deletion
• Object to processing
• File a complaint with the supervisory authority

Contact us for questions about your data.`
      },
      {
        icon: Mail,
        title: 'Contact',
        content: `For questions about this privacy policy, contact us via:
• Email: hello@netblitz.eu
• Phone: +31 85 115 5703
• Address: Robert Schumandomein 2, 6229ES Maastricht, Netherlands`
      }
    ]
  },
  fr: {
    title: 'Politique de confidentialité',
    subtitle: 'Dernière mise à jour: mars 2026',
    intro: 'Chez Netblitz, nous attachons une grande importance à la protection de vos données personnelles. Cette politique de confidentialité explique quelles données nous collectons, pourquoi et comment nous les traitons.',
    sections: [
      {
        icon: Database,
        title: 'Quelles données collectons-nous?',
        content: `Nous collectons les données personnelles suivantes:
• Coordonnées (nom, email, téléphone) via notre formulaire de contact
• Données techniques (adresse IP, type de navigateur) pour l'analyse du site
• Cookies pour la fonctionnalité et l'analyse`
      },
      {
        icon: Eye,
        title: 'Pourquoi collectons-nous ces données?',
        content: `Nous utilisons vos données pour:
• Répondre à vos questions et demandes de devis
• Améliorer notre site web et nos services
• Envoyer des informations pertinentes (uniquement avec votre consentement)`
      },
      {
        icon: Lock,
        title: 'Comment protégeons-nous vos données?',
        content: `Nous prenons des mesures appropriées pour protéger vos données:
• Connexions cryptées (HTTPS)
• Serveurs sécurisés
• Accès limité aux données personnelles
• Mises à jour de sécurité régulières`
      },
      {
        icon: Cookie,
        title: 'Cookies',
        content: `Notre site utilise des cookies pour:
• Cookies nécessaires: pour le fonctionnement du site
• Cookies analytiques: pour comprendre les statistiques de visite
• Cookies marketing: uniquement avec votre consentement explicite

Vous pouvez modifier vos préférences de cookies à tout moment.`
      },
      {
        icon: Shield,
        title: 'Vos droits',
        content: `Vous avez le droit de:
• Demander l'accès à vos données personnelles
• Demander la correction ou la suppression
• Vous opposer au traitement
• Déposer une plainte auprès de l'autorité de contrôle

Contactez-nous pour toute question sur vos données.`
      },
      {
        icon: Mail,
        title: 'Contact',
        content: `Pour toute question sur cette politique de confidentialité:
• Email: hello@netblitz.eu
• Téléphone: +31 85 115 5703
• Adresse: Robert Schumandomein 2, 6229ES Maastricht, Pays-Bas`
      }
    ]
  },
  de: {
    title: 'Datenschutzrichtlinie',
    subtitle: 'Zuletzt aktualisiert: März 2026',
    intro: 'Bei Netblitz legen wir großen Wert auf den Schutz Ihrer persönlichen Daten. Diese Datenschutzrichtlinie erklärt, welche Daten wir sammeln, warum wir sie sammeln und wie wir damit umgehen.',
    sections: [
      {
        icon: Database,
        title: 'Welche Daten sammeln wir?',
        content: `Wir sammeln folgende personenbezogene Daten:
• Kontaktdaten (Name, E-Mail, Telefon) über unser Kontaktformular
• Technische Daten (IP-Adresse, Browsertyp) für Website-Analyse
• Cookies für Funktionalität und Analyse`
      },
      {
        icon: Eye,
        title: 'Warum sammeln wir diese Daten?',
        content: `Wir verwenden Ihre Daten für:
• Beantwortung Ihrer Fragen und Angebotsanfragen
• Verbesserung unserer Website und Dienste
• Versand relevanter Informationen (nur mit Ihrer Zustimmung)`
      },
      {
        icon: Lock,
        title: 'Wie schützen wir Ihre Daten?',
        content: `Wir treffen angemessene Maßnahmen zum Schutz Ihrer Daten:
• Verschlüsselte Verbindungen (HTTPS)
• Sichere Server
• Eingeschränkter Zugang zu personenbezogenen Daten
• Regelmäßige Sicherheitsupdates`
      },
      {
        icon: Cookie,
        title: 'Cookies',
        content: `Unsere Website verwendet Cookies für:
• Notwendige Cookies: für die Website-Funktionalität
• Analytische Cookies: zum Verständnis der Besucherstatistiken
• Marketing-Cookies: nur mit Ihrer ausdrücklichen Zustimmung

Sie können Ihre Cookie-Einstellungen jederzeit anpassen.`
      },
      {
        icon: Shield,
        title: 'Ihre Rechte',
        content: `Sie haben das Recht:
• Auskunft über Ihre personenbezogenen Daten zu verlangen
• Berichtigung oder Löschung zu verlangen
• Der Verarbeitung zu widersprechen
• Eine Beschwerde bei der Aufsichtsbehörde einzureichen

Kontaktieren Sie uns bei Fragen zu Ihren Daten.`
      },
      {
        icon: Mail,
        title: 'Kontakt',
        content: `Bei Fragen zu dieser Datenschutzrichtlinie kontaktieren Sie uns:
• E-Mail: hello@netblitz.eu
• Telefon: +31 85 115 5703
• Adresse: Robert Schumandomein 2, 6229ES Maastricht, Niederlande`
      }
    ]
  }
};

export default function PrivacyPage() {
  const { language } = useLanguage();
  const t = translations[language] || translations.nl;
  const seo = seoContent[language] || seoContent.nl;

  return (
    <main className="min-h-screen bg-[#f8f9fc]" data-testid="privacy-page">
      <SEO 
        title={seo.title}
        description={seo.description}
        noIndex={false}
      />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#2563EB] to-[#0052e0]">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Shield className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-lg opacity-80">{t.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-sm mb-8"
          >
            <p className="text-lg text-[#475569] leading-relaxed">{t.intro}</p>
          </motion.div>

          {/* Sections */}
          <div className="space-y-6">
            {t.sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#0f172a] mb-3">{section.title}</h2>
                    <p className="text-[#475569] whitespace-pre-line leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
